import React, { useState } from 'react';
import { Plus, Filter, Search, List, Grid, Play, Pause, Trash2, GitBranch, Settings, ChevronDown } from 'lucide-react';
import EmptyState from '../components/EmptyState';

const statusOptions = [
  { label: 'All Workflows', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
  { label: 'Draft', value: 'draft' },
];

export default function Workflows() {
  const [viewType, setViewType] = useState('table');
  const [selectedWorkflows, setSelectedWorkflows] = useState(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const workflows = [];

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedWorkflows(new Set(workflows.map((_, index) => index)));
    } else {
      setSelectedWorkflows(new Set());
    }
  };

  const toggleWorkflow = (index: number) => {
    const newSelected = new Set(selectedWorkflows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedWorkflows(newSelected);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'paused':
        return 'bg-yellow-100 text-yellow-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'lead_nurture':
        return <GitBranch className="h-4 w-4 text-blue-500" />;
      case 'customer_retention':
        return <Settings className="h-4 w-4 text-purple-500" />;
      case 'sales':
        return <GitBranch className="h-4 w-4 text-green-500" />;
      case 'feedback':
        return <GitBranch className="h-4 w-4 text-orange-500" />;
      case 'recovery':
        return <GitBranch className="h-4 w-4 text-red-500" />;
      default:
        return <GitBranch className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-gray-900">Workflows</h1>
        <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create Workflow
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search workflows..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>
        <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
        <div className="px-4 py-3 border-b border-[#e1e3e5] flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                {selectedStatus.label}
                <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${isStatusOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStatusOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option);
                          setIsStatusOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          selectedStatus.value === option.value
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-0.5">
            <button
              onClick={() => setViewType('table')}
              className={`p-1.5 rounded-md ${
                viewType === 'table'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewType('grid')}
              className={`p-1.5 rounded-md ${
                viewType === 'grid'
                  ? 'bg-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              <Grid className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center h-96">
          <EmptyState
            icon={<GitBranch className="w-8 h-8 text-gray-400" />}
            title="No workflows yet"
            description="Start creating workflows to automate your business processes."
            actionLabel="Create your first workflow"
            onAction={() => {}}
          />
        </div>
      </div>
    </div>
  );
}